import axios from 'axios';
import { useEffect, useState } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import {
    ScanLine, Keyboard, Printer, CheckCircle2, Ticket, Clock,
    Users, MapPin, CalendarClock, RefreshCw, FileText, Coins,
} from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { Badge, Button, DataTable, Input, PageHeader, RadioGroup, Select, Spin, useAlert } from '../ui';
import { DataTableColumn } from '../ui/DataTable';

type ModeSaisie = 'QR' | 'MANUEL';

/** Convertit une durée en secondes en libellé "X min". */
function formatDuree(sec: any): string {
    const n = Number(sec);
    if (isNaN(n) || n <= 0) return '—';
    return `${Math.max(1, Math.round(n / 60))} min`;
}

/** Formate une heure estimée ("14:30:00" ou date ISO) en "HH:mm". */
function formatHeure(value: any): string {
    if (!value) return '—';
    const s = String(value);
    if (/^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Badge de mode de priorité — tolérant (booléen, code ou libellé). */
function BadgePriorite({ mode }: { mode: any }) {
    const s = String(mode ?? '').toUpperCase();
    if (mode == null || s === '' || s === '0' || s === 'NORMAL' || s === 'FALSE' || s === 'STANDARD')
        return <Badge color="gray">Standard</Badge>;
    if (s.includes('VIP') || s.includes('URGENT')) return <Badge color="red">{s}</Badge>;
    if (s.includes('PRIOR')) return <Badge color="violet">Prioritaire</Badge>;
    return <Badge color="blue">{s}</Badge>;
}

const operationColumns: DataTableColumn[] = [
    { field: 'LibelleOperation', headerName: 'Opération' },
    { field: 'NumeroCompteSaisi', headerName: 'N° compte', width: 140 },
    { field: 'LibelleOrigine', headerName: 'Origine', width: 140 },
    { field: 'Motif', headerName: 'Motif' },
    {
        field: 'MontantDeclare', headerName: 'Montant déclaré', width: 140,
        renderCell: ({ value }) => {
            if (value == null || value === '') return <span>—</span>;
            const n = Number(value);
            return <span>{isNaN(n) ? String(value) : n.toLocaleString('fr-FR')}</span>;
        },
    },
];

export default function CheckIn() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [mode, setMode] = useState<ModeSaisie>('QR')
    const [qrToken, setQrToken] = useState('')
    const [codeT, setCodeT] = useState('')
    const [codeFiliale, setCodeFiliale] = useState(user.CodeFiliale || '')
    const [filiales, setFiliales] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    // Résultat du check-in
    const [ticket, setTicket] = useState<any>(null)
    const [operations, setOperations] = useState<any[]>([])

    // Documents imprimables (formulaires remplis + bordereau de billetage)
    const [docs, setDocs] = useState<{ forms: any[]; hasBilletage: boolean; codeDemande: any }>({ forms: [], hasBilletage: false, codeDemande: null })
    const [printing, setPrinting] = useState('')

    useEffect(() => {
        LoadFiliales()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Charge les filiales pour le Select (pré-sélection = filiale de l'agent). */
    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setFiliales(Array.isArray(res.Result) ? res.Result : [])
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    /** POST CheckInTicket — confirme l'arrivée du client (statut '0' -> '1'). */
    function Valider() {
        if (!codeFiliale) {
            alert.warning('Veuillez sélectionner une filiale')
            return
        }
        if (mode === 'QR' && !qrToken.trim()) {
            alert.warning('Veuillez scanner ou coller le token QR')
            return
        }
        if (mode === 'MANUEL' && !codeT.trim()) {
            alert.warning('Veuillez saisir le numéro de ticket')
            return
        }

        setLoading(true)
        const payload = mode === 'QR'
            ? { QrToken: qrToken.trim(), CodeFiliale: codeFiliale }
            : { CodeT: codeT.trim(), CodeFiliale: codeFiliale }

        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Demandes.CheckInTicket,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    const result = res.Result || {}
                    const tickets = Array.isArray(result) ? result : (result.Ticket || result.Tickets || [])
                    setTicket(tickets[0] || null)
                    setOperations(Array.isArray(result.Operations) ? result.Operations : [])
                    loadDocs(tickets[0])
                    alert.success(res.Message || 'Arrivée confirmée')
                }
                else {
                    // 404 introuvable | 208 déjà confirmé/appelé | 400 paramètres manquants
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error)
                console.log(error);
            });
    }

    /** Réinitialise le formulaire pour un nouveau check-in. */
    function NouveauCheckIn() {
        setTicket(null)
        setOperations([])
        setDocs({ forms: [], hasBilletage: false, codeDemande: null })
        setQrToken('')
        setCodeT('')
    }

    /** Ouvre le PDF renvoyé en Base64 ({FileName, ContentType, Base64}). */
    function openPdfBase64(result: any) {
        const bytes = Uint8Array.from(atob(result.Base64), (c) => c.charCodeAt(0))
        const blob = new Blob([bytes], { type: result.ContentType || 'application/pdf' })
        window.open(URL.createObjectURL(blob), '_blank')
    }

    /** GET TicketDetails — formulaires remplis + billetage de la demande. */
    function loadDocs(t: any) {
        const codeT = t?.CodeT
        if (!codeT) return
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Tickets.TicketDetails + Crypter.encrypt(codeT),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((response: any) => {
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            if (res.StatutCode == 200) {
                let r = res.Result
                if (typeof r === 'string') { try { r = JSON.parse(r) } catch { } }
                setDocs({
                    forms: Array.isArray(r?.Formulaires) ? r.Formulaires : [],
                    hasBilletage: !!(r?.Billetage || r?.HasBilletage || (Array.isArray(r?.Lignes) && r.Lignes.length)),
                    codeDemande: r?.CodeDemande || t?.CodeDemande,
                })
            }
        }).catch(() => { /* documents indisponibles — non bloquant */ })
    }

    /** POST Formulaires/Imprimer — PDF du formulaire rempli pour cette demande. */
    function imprimerFormulaire(f: any) {
        setPrinting(f.CodeForm)
        axios.request({
            method: 'post', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Formulaires.Imprimer,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
            data: Crypter.encrypt(JSON.stringify({
                CodeForm: f.CodeForm,
                CodeAbonne: user.CodeAbonne,
                CodeFiliale: codeFiliale || undefined,
                CodeDemande: f.CodeDemande || docs.codeDemande || undefined,
                Orientation: 'PORTRAIT',
            })),
        }).then((response: any) => {
            setPrinting('')
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            if (res.StatutCode == 200 && res.Result?.Base64) openPdfBase64(res.Result)
            else alert.warning(res.Message || 'PDF non généré')
        }).catch((error: any) => {
            setPrinting('')
            alert.error('Error', error?.message || error)
        })
    }

    /** GET Billetage/Bordereau — bordereau PDF du billetage de la demande. */
    function imprimerBordereau() {
        if (!docs.codeDemande) return
        setPrinting('bordereau')
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Billetage.Bordereau + Crypter.encrypt(docs.codeDemande),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((response: any) => {
            setPrinting('')
            const res = JSON.parse(Crypter.decrypt(response.data)).Value
            if (res.StatutCode == 200 && res.Result?.Base64) openPdfBase64(res.Result)
            else alert.warning(res.Message || 'Bordereau non généré')
        }).catch((error: any) => {
            setPrinting('')
            alert.error('Error', error?.message || error)
        })
    }

    const client = [ticket?.NomClient, ticket?.PrenomClient].filter(Boolean).join(' ')

    return (
        <Spin spinning={loading} >
            {/* Styles d'impression : seule la carte ticket est imprimée */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .eb-print-ticket, .eb-print-ticket * { visibility: visible; }
                    .eb-print-ticket { position: absolute; top: 0; left: 0; width: 100%; }
                }
            `}</style>

            <PageHeader
                title="Check-in arrivée"
                subtitle="Confirmez l'arrivée d'un client : scan du QR ou saisie du numéro de ticket"
            />

            {!ticket ? (
                /* ---------- Formulaire de check-in ---------- */
                <div className="eb-card max-w-xl mx-auto p-6">
                    <RadioGroup
                        label="Mode de check-in"
                        value={mode}
                        onChange={(v) => setMode(v as ModeSaisie)}
                        options={[
                            { value: 'QR', label: <span className="inline-flex items-center gap-1.5"><ScanLine size={15} />Scan QR</span> },
                            { value: 'MANUEL', label: <span className="inline-flex items-center gap-1.5"><Keyboard size={15} />Saisie manuelle</span> },
                        ]}
                    />

                    <div className="mt-4 space-y-4">
                        {mode === 'QR' ? (
                            <Input
                                key="qr"
                                autoFocus
                                label="Token QR"
                                placeholder="Scannez ou collez le token QR"
                                value={qrToken}
                                onChange={(e: any) => setQrToken(e.target.value)}
                                onKeyDown={(e: any) => { if (e.key === 'Enter') Valider() }}
                                startIcon={<ScanLine size={15} />}
                            />
                        ) : (
                            <Input
                                key="manuel"
                                autoFocus
                                label="Numéro de ticket"
                                placeholder="GS1-007"
                                value={codeT}
                                onChange={(e: any) => setCodeT(e.target.value)}
                                onKeyDown={(e: any) => { if (e.key === 'Enter') Valider() }}
                                startIcon={<Ticket size={15} />}
                            />
                        )}

                        <Select
                            label="Filiale"
                            value={codeFiliale}
                            onChange={setCodeFiliale}
                            options={filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle}` }))}
                            placeholder="Sélectionner une filiale"
                        />

                        <Button
                            variant="primary"
                            size="lg"
                            className="w-full"
                            loading={loading}
                            startIcon={<CheckCircle2 size={18} />}
                            onClick={Valider}
                        >Valider le check-in</Button>
                    </div>
                </div>
            ) : (
                /* ---------- Ticket résultat ---------- */
                <div className="max-w-2xl mx-auto">
                    <div className="eb-print-ticket eb-card border-2 border-dashed border-[var(--eb-border)] p-6 text-center">
                        <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--eb-text-muted)]">
                            <Ticket size={14} />
                            Ticket de file d'attente
                        </div>

                        <div className="mt-3 font-mono text-4xl font-bold text-[var(--eb-text)]">
                            {ticket.NumeroTicket || ticket.CodeT || '—'}
                        </div>

                        <div className="mt-2 text-sm font-medium text-[var(--eb-text)]">
                            {ticket.LibelleService || '—'}
                        </div>
                        <div className="mt-1 flex items-center justify-center gap-1.5 text-xs text-[var(--eb-text-muted)]">
                            <MapPin size={13} />
                            {[ticket.LibelleFiliale || ticket.CodeFiliale, ticket.LocationFiliale].filter(Boolean).join(' — ')}
                        </div>

                        {(client || ticket.Canal || ticket.CodeDemande) && (
                            <div className="mt-4 flex items-center justify-center gap-2 flex-wrap text-xs text-[var(--eb-text-muted)]">
                                {client && <span className="inline-flex items-center gap-1"><Users size={13} />{client}</span>}
                                {ticket.Canal && <Badge color="blue">{ticket.Canal}</Badge>}
                                {ticket.CodeDemande && <span className="font-mono">{ticket.CodeDemande}</span>}
                            </div>
                        )}

                        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                            <div>
                                <div className="text-[11px] font-semibold uppercase text-[var(--eb-text-muted)]">Position</div>
                                <div className="mt-1 text-lg font-bold text-[var(--eb-text)]">{ticket.Position ?? '—'}</div>
                            </div>
                            <div>
                                <div className="text-[11px] font-semibold uppercase text-[var(--eb-text-muted)]">Devant vous</div>
                                <div className="mt-1 text-lg font-bold text-[var(--eb-text)]">{ticket.NbTicketsDevant ?? '—'}</div>
                            </div>
                            <div>
                                <div className="text-[11px] font-semibold uppercase text-[var(--eb-text-muted)]"><Clock size={11} className="inline mr-1" />Attente estimée</div>
                                <div className="mt-1 text-lg font-bold text-[var(--eb-text)]">{formatDuree(ticket.TempsAttenteEstimeSec)}</div>
                            </div>
                            <div>
                                <div className="text-[11px] font-semibold uppercase text-[var(--eb-text-muted)]"><CalendarClock size={11} className="inline mr-1" />Appel estimé</div>
                                <div className="mt-1 text-lg font-bold text-[var(--eb-text)]">{formatHeure(ticket.HeureAppelEstimee)}</div>
                            </div>
                        </div>

                        <div className="mt-4">
                            <BadgePriorite mode={ticket.ModePriorite} />
                        </div>
                    </div>

                    {/* Opérations rattachées à la demande */}
                    {operations.length > 0 && (
                        <div className="eb-card mt-4 p-4">
                            <h3 className="text-sm font-bold text-[var(--eb-text)] mb-3">Opérations demandées</h3>
                            <DataTable
                                rows={operations.map((o: any, i: number) => ({ id: o.CodeOperationLigne ?? i, ...o }))}
                                columns={operationColumns}
                                height={220}
                                searchable={false}
                                emptyText="Aucune opération"
                            />
                        </div>
                    )}

                    {/* Documents imprimables : formulaires remplis + bordereau de billetage */}
                    {(docs.forms.length > 0 || docs.hasBilletage) && (
                        <div className="eb-card mt-4 p-4">
                            <h3 className="text-sm font-bold text-[var(--eb-text)] mb-3">Documents de la demande</h3>
                            <div className="flex flex-col gap-2">
                                {docs.forms.map((f: any) => (
                                    <Button
                                        key={f.CodeForm}
                                        variant="outline"
                                        size="sm"
                                        loading={printing === f.CodeForm}
                                        startIcon={<FileText size={15} />}
                                        onClick={() => imprimerFormulaire(f)}
                                    >{f.Libelle || f.CodeForm}</Button>
                                ))}
                                {docs.hasBilletage && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        loading={printing === 'bordereau'}
                                        startIcon={<Coins size={15} />}
                                        onClick={imprimerBordereau}
                                    >Bordereau de billetage</Button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mt-5 flex items-center justify-center gap-3">
                        <Button
                            variant="outline"
                            startIcon={<Printer size={16} />}
                            onClick={() => window.print()}
                        >Imprimer le ticket</Button>
                        <Button
                            variant="primary"
                            startIcon={<RefreshCw size={16} />}
                            onClick={NouveauCheckIn}
                        >Nouveau check-in</Button>
                    </div>
                </div>
            )}
        </Spin>
    );
}
